import { LinkIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { useToast } from 'src/context/ToastContext';
import { useCopyToClipboard } from 'src/hooks/useCopyToClipboard';

/**
 * Copies an absolute URL that opens one agent run. On the agent page the
 * address bar becomes that link once a run is selected; this is the button to
 * press while filing a ticket, including when the page is still following the
 * newest run and the URL has not been pinned yet.
 */
export function CopyAgentRunLinkButton({ url }: { url: string }) {
  const gt = useGT();
  const { showToast } = useToast();
  const { copy } = useCopyToClipboard();

  async function handleClick() {
    const copied = await copy(url);
    if (copied) {
      showToast({
        color: 'success',
        title: gt('Link copied', {
          context: 'toast title after copying a link to an agent run',
        }),
        message: gt('Paste it into a ticket to open this run.', {
          context: 'toast after copying a link to one agent run',
        }),
      });
      return;
    }
    showToast({
      color: 'error',
      title: gt('Could not copy link', {
        context: 'toast title when copying an agent run link fails',
      }),
      message: gt('Clipboard access was blocked. Try again.', {
        context:
          'toast when the clipboard is unavailable for an agent run link',
      }),
    });
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="shrink-0"
      onClick={() => void handleClick()}
    >
      <LinkIcon className="mr-1 h-4 w-4" />
      <T context="button that copies a URL to this agent run">Copy Link</T>
    </Button>
  );
}
